import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import {
  ChartCard,
  RangePicker,
  type Row,
  SERIES_COLORS,
  type Series,
  TimeChart,
} from "@/components/charts";
import { Button } from "@/components/ui/button";
import { CapacityPanel } from "@/features/admin/capacity";
import { Empty, PageHeader, Rows, UserLink } from "@/features/admin/parts";
import type { Schemas } from "@/lib/api/client";
import {
  capacityQuery,
  dashboardQuery,
  hostMetricsQuery,
  type MetricsRange,
  topConfigsQuery,
  useSyncNow,
} from "@/lib/api/queries";
import { cn } from "@/lib/cn";
import { formatBitrate, formatBytes, formatMbit, formatPercent } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(dashboardQuery),
      context.queryClient.ensureQueryData(capacityQuery),
    ]),
  component: Dashboard,
});

const RANGES = ["1h", "24h", "7d", "30d", "90d"] as const satisfies readonly MetricsRange[];

function Dashboard() {
  const { data } = useSuspenseQuery(dashboardQuery);
  const { data: capacity } = useSuspenseQuery(capacityQuery);
  const sync = useSyncNow();
  const { server, configs_by_status: byStatus } = data;

  return (
    <>
      <PageHeader
        title="Обзор"
        actions={
          <Button
            variant="secondary"
            size="sm"
            pending={sync.isPending}
            onClick={() =>
              sync.mutate(undefined, {
                onSuccess: () => toast.success("Синхронизировано с сервером"),
                onError: (error) => toast.error(error.message),
              })
            }
          >
            <RefreshCw aria-hidden />
            Синхронизировать
          </Button>
        }
      />

      <p
        className={cn(
          "mt-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm",
          server.reachable ? "bg-online/10" : "bg-danger/10 text-danger",
        )}
      >
        <span
          aria-hidden
          className={cn("size-2 rounded-full", server.reachable ? "bg-online" : "bg-danger")}
        />
        {server.reachable
          ? `Контейнер AmneziaWG на связи${server.requires_awg2_client ? ", протокол AWG 2" : ""}`
          : "Нет связи с контейнером AmneziaWG — создание и отзыв конфигов недоступны"}
      </p>

      <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3">
        <Stat label="Пользователей" value={data.users} />
        <Stat label="Онлайн сейчас" value={data.online} accent />
        <Stat label="Активных конфигов" value={byStatus.active ?? 0} />
        <Stat label="Свободных IP" value={server.free_addresses ?? "—"} />
        <Stat label="Получено клиентами" value={formatBytes(data.tx_bytes)} />
        <Stat label="Отправлено клиентами" value={formatBytes(data.rx_bytes)} />
      </dl>

      {(byStatus.suspended ?? 0) + (byStatus.revoked_externally ?? 0) > 0 && (
        <p className="mt-4 text-sm text-muted">
          Приостановлено: {byStatus.suspended ?? 0}. Удалено в приложении Amnezia:{" "}
          {byStatus.revoked_externally ?? 0}.
        </p>
      )}

      <div className="mt-8">
        <CapacityPanel capacity={capacity} />
      </div>

      <LoadSection limitBps={capacity.effective_limit_bps ?? capacity.link_capacity_bps} />
    </>
  );
}

type Point = Schemas["HostPointOut"];

const toRows = (points: readonly Point[], pick: (p: Point) => Record<string, number | null>) =>
  points.map((p): Row => ({ t: new Date(p.at).getTime(), ...pick(p) }));

const LINK_SERIES: Series[] = [
  { key: "avg", label: "Средняя за интервал", color: SERIES_COLORS[0] },
  { key: "peak", label: "Всплески (15 с)", color: SERIES_COLORS[1], kind: "line" },
];
const VPN_SERIES: Series[] = [
  { key: "down", label: "Скачивают", color: SERIES_COLORS[0] },
  { key: "up", label: "Отдают", color: SERIES_COLORS[2] },
];
const CPU_SERIES: Series[] = [
  { key: "cpu", label: "Весь сервер", color: SERIES_COLORS[0] },
  { key: "awg", label: "amneziawg-go", color: SERIES_COLORS[2], kind: "line" },
  { key: "steal", label: "Забрал гипервизор", color: SERIES_COLORS[1], kind: "line" },
];
const PEER_SERIES: Series[] = [
  { key: "online", label: "Онлайн", color: SERIES_COLORS[0] },
  { key: "active", label: "Качают трафик", color: SERIES_COLORS[2], kind: "line" },
];

function LoadSection({ limitBps }: { limitBps: number }) {
  const [range, setRange] = useState<MetricsRange>("24h");
  const { data, isPending } = useQuery({
    ...hostMetricsQuery(range),
    placeholderData: keepPreviousData,
  });
  const points = data?.points ?? [];
  const bucket = data?.bucket_seconds ?? 60;
  const last = points.at(-1);
  const common = { range, bucketSeconds: bucket };

  return (
    <section aria-labelledby="load-title" className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="load-title" className="text-base">
          Нагрузка
        </h2>
        <RangePicker label="Период графиков" value={range} onChange={setRange} options={RANGES} />
      </div>

      {!isPending && points.length === 0 ? (
        <div className="mt-4">
          <Empty>Замеров за этот период пока нет — первые точки появятся через минуту-две.</Empty>
        </div>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <ChartCard
            title="Канал сервера, Мбит/с"
            summary={
              last && `Сейчас ${formatBitrate(Math.max(last.link_rx_bps, last.link_tx_bps))}`
            }
            series={LINK_SERIES}
          >
            <TimeChart
              {...common}
              rows={toRows(points, (p) => ({
                avg: Math.max(p.link_rx_bps, p.link_tx_bps),
                peak: p.link_peak_bps,
              }))}
              series={LINK_SERIES}
              format={formatBitrate}
              tickFormat={formatMbit}
              limit={{ value: limitBps, label: `предел ${formatBitrate(limitBps)}` }}
            />
          </ChartCard>
          <ChartCard
            title="Трафик клиентов, Мбит/с"
            summary={last && `Сейчас скачивают ${formatBitrate(last.vpn_tx_bps)}`}
            series={VPN_SERIES}
          >
            <TimeChart
              {...common}
              rows={toRows(points, (p) => ({ down: p.vpn_tx_bps, up: p.vpn_rx_bps }))}
              series={VPN_SERIES}
              format={formatBitrate}
              tickFormat={formatMbit}
            />
          </ChartCard>
          <ChartCard
            title="Процессор, %"
            summary={last && `Сейчас ${formatPercent(last.cpu_pct)}`}
            series={CPU_SERIES}
          >
            <TimeChart
              {...common}
              rows={toRows(points, (p) => ({
                cpu: p.cpu_pct,
                awg: p.awg_cpu_pct,
                steal: p.steal_pct,
              }))}
              series={CPU_SERIES}
              format={formatPercent}
              tickFormat={(v) => String(v)}
              yMax={100}
              limit={{ value: 85, label: "85%" }}
            />
          </ChartCard>
          <ChartCard
            title="Устройства"
            summary={last && `Сейчас онлайн ${last.online_peers}`}
            series={PEER_SERIES}
          >
            <TimeChart
              {...common}
              rows={toRows(points, (p) => ({ online: p.online_peers, active: p.active_peers }))}
              series={PEER_SERIES}
              format={(v) => String(Math.round(v))}
              tickFormat={(v) => String(v)}
            />
          </ChartCard>
        </div>
      )}

      <TopConfigs range={range === "1h" ? "24h" : range} />
    </section>
  );
}

const RANGE_TITLES: Record<MetricsRange, string> = {
  "1h": "за час",
  "24h": "за сутки",
  "7d": "за неделю",
  "30d": "за месяц",
  "90d": "за 3 месяца",
};

function TopConfigs({ range }: { range: MetricsRange }) {
  const { data } = useQuery({ ...topConfigsQuery(range), placeholderData: keepPreviousData });
  if (!data) return null;
  const max = Math.max(1, ...data.map((item) => item.rx_bytes + item.tx_bytes));

  return (
    <section aria-labelledby="top-title" className="mt-8">
      <h2 id="top-title" className="text-base">
        Больше всего трафика {RANGE_TITLES[range]}
      </h2>
      <div className="mt-3">
        {data.length === 0 ? (
          <Empty>Трафика за этот период нет.</Empty>
        ) : (
          <Rows>
            {data.map((item) => {
              const total = item.rx_bytes + item.tx_bytes;
              return (
                <li
                  key={item.config_id}
                  className="grid gap-1 px-4 py-3 sm:grid-cols-[1fr_12rem_7rem] sm:items-center sm:gap-4"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{item.name}</p>
                    <p className="truncate text-sm text-muted">
                      {item.user_id === null ? (
                        "без владельца"
                      ) : (
                        <UserLink id={item.user_id} username={item.username} />
                      )}
                    </p>
                  </div>
                  <div aria-hidden className="h-1.5 rounded-full bg-surface-2">
                    <div
                      className="h-full rounded-full bg-series-1"
                      style={{ width: `${(total / max) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm tabular-nums sm:text-right">{formatBytes(total)}</p>
                </li>
              );
            })}
          </Rows>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, accent }: { label: string; value: ReactNode; accent?: boolean }) {
  return (
    <div className="bg-surface px-4 py-4">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className={cn("mt-1 font-display text-lg", accent && "text-online")}>{value}</dd>
    </div>
  );
}
